import React from 'react';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import Icon from '../../ui/Icon';
import EmptyState from '../../ui/EmptyState';
import { ICONS } from '../../ui/icon-map';
import { Field, Input } from '../../ui/Field/Field';
import { usePermissions } from '../../hooks/usePermissions';

// the documents (PDF, the template, the Excel upload): their own permission on every government page (permissions
// phase 4, 4 Oct 2026)
const useCanGenerate = () => usePermissions().hasPermission('gov_generate_documents');

/*
 * On-screen building blocks shared by the government document pages
 * (Vehicle Export, Hiaza, Ownership Transfer, Free Document, Registration, Sales Invoice).
 * Presentation only: every handler and value comes from the page.
 */

/** Excel upload: download the template or pick a file (hidden input stays in the page's ref). */
export function VehicleUploadCard({ title = 'Upload New Vehicles', description = 'Please upload an Excel file containing Plate Numbers or VINs in any column.', onDownloadTemplate, fileInputRef, onFileChange, accept = '.xlsx, .xls, .csv', children }) {
    const canGenerate = useCanGenerate();
    if (!canGenerate) return children ? <>{children}</> : null;
    return (
        <Card title={title} subtitle={description}>
            <div className="flex flex-wrap gap-3">
                {onDownloadTemplate && (
                    <Button
                        variant="secondary"
                        onClick={onDownloadTemplate}
                        leadingIcon={<Icon glyph={ICONS.download} size="sm" />}
                    >
                        Download Template
                    </Button>
                )}
                <input
                    type="file"
                    accept={accept}
                    style={{ display: 'none' }}
                    ref={fileInputRef}
                    onChange={onFileChange}
                    aria-label="Excel file"
                />
                <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    leadingIcon={<Icon glyph={ICONS.upload} size="sm" />}
                >
                    Select Excel File
                </Button>
            </div>
            {children}
        </Card>
    );
}

/** Search the fleet and add a vehicle. Enter adds the first match (same as before). */
export function VehicleSearchCard({ title = 'Add Vehicles Manually', searchQuery, onSearchChange, results, onSelect, showYear = false, placeholder = 'Search by Plate, Make or VIN...', actionLabel = 'Select', children }) {
    return (
        <Card title={title}>
            <div className="space-y-3">
                <Field label="Search vehicles">
                    <Input
                        type="search"
                        placeholder={placeholder}
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && results.length > 0) {
                                onSelect(results[0]);
                            }
                        }}
                        autoComplete="off"
                    />
                </Field>

                {searchQuery && (
                    <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                        {results.length > 0 ? (
                            <ul className="divide-y divide-gray-100">
                                {results.map(car => (
                                    <li key={car.plate_number}>
                                        <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 flex justify-between items-center gap-3 hover:bg-gray-50 focus-visible:outline-none focus-visible:bg-purple-50"
                                            onClick={() => onSelect(car)}
                                        >
                                            <span className="min-w-0">
                                                <span className="block text-sm font-medium text-gray-900">{car.plate_number}</span>
                                                <span className="block text-xs text-gray-500">{car.make} {car.model}{showYear ? ` (${car.year})` : ''}</span>
                                            </span>
                                            <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 shrink-0">
                                                <Icon glyph={ICONS.plus} size="xs" />
                                                {actionLabel}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="p-4 text-center text-sm text-gray-500">No results found</div>
                        )}
                    </div>
                )}
                {children}
            </div>
        </Card>
    );
}

const byPlate = (car) => car.plate_number;
const makeModel = (car) => `${car.make} ${car.model}`;
const plateTail = (car) => String(car.plate_number).slice(-3);

/**
 * The selected vehicles with Remove / Remove All and the Generate PDF action.
 * itemKey / removeKey / primary / secondary / tile read a row (defaults: fleet cars by plate).
 */
export function SelectedVehiclesCard({
    selectedCars,
    onRemove,
    onClearAll,
    onGenerate,
    generating,
    numbered = false,
    generateLabel = 'Generate PDF',
    title,
    itemKey = byPlate,
    removeKey = byPlate,
    primary = byPlate,
    secondary = makeModel,
    tile = plateTail,
    emptyTitle = 'No vehicles selected yet',
    emptyDescription = 'Upload an Excel file or search the fleet to add vehicles.',
    renderMeta,
}) {
    const canGenerate = useCanGenerate();
    return (
        <Card
            title={title || `Selected Vehicles (${selectedCars.length})`}
            actions={
                <>
                    {selectedCars.length > 0 && (
                        <Button
                            onClick={onClearAll}
                            variant="ghost"
                            size="sm"
                            leadingIcon={<Icon glyph={ICONS.delete} size="sm" />}
                        >
                            Remove All
                        </Button>
                    )}
                    {canGenerate && <Button
                        onClick={onGenerate}
                        disabled={selectedCars.length === 0 || generating}
                        isLoading={generating}
                        variant="primary"
                        size="sm"
                        leadingIcon={<Icon glyph={ICONS.export} size="sm" />}
                    >
                        {generating ? 'Generating...' : generateLabel}
                    </Button>}
                </>
            }
        >
            {selectedCars.length > 0 ? (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
                    {selectedCars.map((car, idx) => (
                        <li key={itemKey(car)} className="flex items-center justify-between gap-3 px-3 py-2">
                            <div className="flex items-center gap-3 min-w-0">
                                {numbered && (
                                    <span className="text-xs text-gray-500 w-5 tabular-nums">{idx + 1}.</span>
                                )}
                                <span className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center text-gray-700 text-xs font-semibold font-mono shrink-0" aria-hidden="true">
                                    {tile(car)}
                                </span>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">{primary(car)}</div>
                                    <div className="text-xs text-gray-500 truncate">{secondary(car)}</div>
                                    {renderMeta && renderMeta(car, idx)}
                                </div>
                            </div>
                            <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => onRemove(removeKey(car))}
                                aria-label={`Remove ${primary(car)}`}
                            >
                                Remove
                            </Button>
                        </li>
                    ))}
                </ul>
            ) : (
                <EmptyState
                    icon={ICONS.fleet}
                    title={emptyTitle}
                    description={emptyDescription}
                />
            )}
        </Card>
    );
}
